import { useMemo } from 'react'
import { Link } from 'react-router'
import { Badge } from '#app/components/ui/badge.tsx'
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from '#app/components/ui/card.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { formatPrice } from '#app/utils/currency.ts'
import {
	DUMPSTER_SIZES,
	matchListingToSize,
} from '../dumpster-size-calculator.logic.ts'
import { DumpsterSizeChart } from './dumpster-size-chart.tsx'

type Listing = {
	id: string
	title: string
	slug: string
	dailyRateCents: number | null
	weeklyRateCents: number | null
	monthlyRateCents: number | null
	subcategories: Array<{ slug: string; name: string }>
	cities: Array<{ id: string; name: string; slug: string }>
	ownerBusiness: {
		name: string
		slug: string
		phone: string | null
		website: string | null
	} | null
}

const SIZE_WIDTH_PERCENT: Record<number, string> = {
	10: 'w-1/4',
	15: 'w-2/5',
	20: 'w-1/2',
	30: 'w-3/4',
	40: 'w-full',
}

const SIZE_BORDER_COLOR: Record<number, string> = {
	10: 'border-t-sky-500',
	15: 'border-t-blue-500',
	20: 'border-t-indigo-500',
	30: 'border-t-violet-500',
	40: 'border-t-purple-500',
}

const SIZE_BAR_COLOR: Record<number, string> = {
	10: 'bg-sky-500',
	15: 'bg-blue-500',
	20: 'bg-indigo-500',
	30: 'bg-violet-500',
	40: 'bg-purple-500',
}

function getSizeStats(listings: Listing[], subcategorySlug: string) {
	const matched = listings.filter(
		(l) => matchListingToSize(l) === subcategorySlug,
	)
	const prices = matched
		.map((l) => l.weeklyRateCents ?? l.dailyRateCents)
		.filter((p): p is number => p != null && p > 0)

	const vendors = [
		...new Set(
			matched
				.map((l) => l.ownerBusiness)
				.filter((b): b is NonNullable<typeof b> => b != null)
				.map((b) => b.name),
		),
	]

	return {
		lowPrice: prices.length > 0 ? Math.min(...prices) : null,
		highPrice: prices.length > 0 ? Math.max(...prices) : null,
		vendors,
		count: matched.length,
	}
}

export function DumpsterSizeGuide({ listings }: { listings: Listing[] }) {
	const sizeStats = useMemo(() => {
		const stats = new Map<
			string,
			ReturnType<typeof getSizeStats>
		>()
		for (const size of DUMPSTER_SIZES) {
			stats.set(size.subcategorySlug, getSizeStats(listings, size.subcategorySlug))
		}
		return stats
	}, [listings])

	return (
		<section>
			<Badge variant="secondary" className="mb-3">
				Size Reference
			</Badge>
			<h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
				DFW Dumpster Sizes & Dimensions
			</h2>
			<p className="text-muted-foreground mt-2">
				Compare dumpster sizes side-by-side with real Dallas-Fort Worth pricing
				and vendor availability.
			</p>

			<div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
				{DUMPSTER_SIZES.map((size) => {
					const stats = sizeStats.get(size.subcategorySlug)
					return (
						<Card key={size.value} className={`border-t-4 ${SIZE_BORDER_COLOR[size.value]} transition-shadow hover:shadow-md`}>
							<CardHeader>
								<div className="flex items-center justify-between">
									<CardTitle>{size.label} Dumpster</CardTitle>
									<Badge variant="secondary">{size.cubicYards} cu yd</Badge>
								</div>
							</CardHeader>
							<CardContent className="space-y-4">
								<div className="space-y-1 text-sm">
									<p>
										<span className="text-muted-foreground">Dimensions:</span>{' '}
										{size.dimensions}
									</p>
									<p>
										<span className="text-muted-foreground">
											Equivalent to:
										</span>{' '}
										{size.truckLoads}
									</p>
								</div>

								{/* DFW price range */}
								{stats && stats.lowPrice != null && (
									<div className="rounded-md bg-green-50 px-3 py-2 dark:bg-green-950/30">
										<p className="flex items-center gap-1.5 text-sm font-medium">
											<Icon name="badge-dollar-sign" size="sm" className="text-green-600 dark:text-green-400" />
											<span className="text-green-700 dark:text-green-300">
												DFW Price:{' '}
												{stats.lowPrice === stats.highPrice
													? formatPrice(stats.lowPrice)
													: `${formatPrice(stats.lowPrice)} – ${formatPrice(stats.highPrice!)}`}
											</span>
										</p>
										{stats.vendors.length > 0 && (
											<p className="text-muted-foreground mt-0.5 text-xs">
												Available from {stats.vendors.join(', ')}
											</p>
										)}
									</div>
								)}

								{/* Relative size indicator */}
								<div className="bg-muted h-3 w-full rounded-full">
									<div
										className={`h-3 rounded-full ${SIZE_BAR_COLOR[size.value]} ${SIZE_WIDTH_PERCENT[size.value]}`}
									/>
								</div>

								<div>
									<p className="text-muted-foreground mb-1 text-xs font-medium uppercase tracking-wide">
										Best for
									</p>
									<ul className="space-y-1 text-sm">
										{size.bestFor.map((item, idx) => (
											<li key={idx} className="text-muted-foreground flex items-start gap-1.5">
												<Icon name="check" size="sm" className="mt-0.5 shrink-0 text-green-500" />
												{item}
											</li>
										))}
									</ul>
								</div>
							</CardContent>
						</Card>
					)
				})}
			</div>

			{/* Visual size chart for image SEO — "dumpster sizes chart" */}
			<DumpsterSizeChart listings={listings} />

			{/* Project-specific subsections for SEO long-tail keywords */}
			<div className="mt-12 space-y-8">
				<div>
					<div className="flex items-center gap-3">
						<div className="bg-primary/10 flex size-10 items-center justify-center rounded-full">
							<Icon name="home" size="md" className="text-primary" />
						</div>
						<h3 className="text-xl font-semibold">
							What Size Dumpster for a Roof Tear-Off?
						</h3>
					</div>
					<p className="text-muted-foreground mt-2 pl-[52px]">
						For a standard single-layer residential roof tear-off (up to 2,000
						sq ft), a <strong>20-yard dumpster</strong> is the most common
						choice in the DFW area. Shingles are heavy — a single layer of
						asphalt shingles from a 2,000 sq ft roof weighs approximately 2-3
						tons. Most Dallas-Fort Worth vendors recommend a 15-20 yard dumpster
						for roofing projects, with some limiting shingle loads to smaller
						sizes due to weight restrictions. For multi-layer tear-offs or roofs
						over 3,000 sq ft, consider a 30-yard dumpster.
					</p>
				</div>

				<div>
					<div className="flex items-center gap-3">
						<div className="bg-primary/10 flex size-10 items-center justify-center rounded-full">
							<Icon name="package" size="md" className="text-primary" />
						</div>
						<h3 className="text-xl font-semibold">
							What Size Dumpster to Clean Out a House?
						</h3>
					</div>
					<p className="text-muted-foreground mt-2 pl-[52px]">
						The dumpster size for a house cleanout depends on the scope. For a
						few rooms or a garage cleanout, a{' '}
						<strong>10-15 yard dumpster</strong> is usually enough. A full house
						cleanout — clearing out all furniture, appliances, and accumulated
						items — typically requires a <strong>20-yard dumpster</strong> for a
						2-3 bedroom home or a <strong>30-yard dumpster</strong> for larger
						homes or estate cleanouts. When in doubt, size up — it&apos;s more
						cost-effective than needing a second dumpster.
					</p>
				</div>

				<div>
					<div className="flex items-center gap-3">
						<div className="bg-primary/10 flex size-10 items-center justify-center rounded-full">
							<Icon name="forklift" size="md" className="text-primary" />
						</div>
						<h3 className="text-xl font-semibold">
							What Size Dumpster Do I Need for Furniture?
						</h3>
					</div>
					<p className="text-muted-foreground mt-2 pl-[52px]">
						For furniture disposal, a <strong>10-15 yard dumpster</strong>{' '}
						handles most situations. A 10-yard fits roughly 3-4 large furniture
						pieces (sofas, dressers, mattresses) plus smaller items. If
						you&apos;re clearing out an entire household of furniture or
						combining with other debris, step up to a 15 or 20-yard. A single
						couch or mattress disposal may be more cost-effective with a{' '}
						<Link
							to="/rentals/category/junk-removal"
							className="text-primary hover:underline"
						>
							junk removal service
						</Link>{' '}
						instead of a full dumpster rental.
					</p>
				</div>

				<div>
					<div className="flex items-center gap-3">
						<div className="bg-primary/10 flex size-10 items-center justify-center rounded-full">
							<Icon name="drill" size="md" className="text-primary" />
						</div>
						<h3 className="text-xl font-semibold">
							What Size Dumpster for Concrete & Heavy Debris?
						</h3>
					</div>
					<p className="text-muted-foreground mt-2 pl-[52px]">
						Concrete, brick, dirt, and other heavy materials are extremely dense.
						Most DFW vendors limit heavy debris loads to{' '}
						<strong>10-15 yard dumpsters</strong> because you&apos;ll hit the
						weight limit long before filling a larger container. A 10-yard
						dumpster loaded with concrete can weigh 10+ tons. Always confirm the
						weight limit with your vendor, and expect to pay overage fees of
						$50-$100 per extra ton if you exceed the included weight allowance.
					</p>
				</div>
			</div>
		</section>
	)
}
